import type { GanttData, ProjectMeta } from '../types';
import { DEFAULT_META } from '../types';
import { LEGACY_GROUP_MIGRATION } from '../config/categories';

const STORAGE_KEY = 'tb-gantt:v1';

export interface Draft {
  id: string;
  name: string;            // human-readable label
  meta: ProjectMeta;
  gantt: GanttData;
  gridStart: string;       // user-overridden grid range (or '')
  gridEnd: string;
  tsvStart: string;
  tsvEnd: string;
  lastModified: number;
}

export interface DraftStore {
  currentId: string;
  drafts: Record<string, Draft>;
}

export function newDraft(name = '新案件'): Draft {
  const id = `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  return {
    id,
    name,
    meta: { ...DEFAULT_META },
    gantt: {},
    gridStart: '',
    gridEnd: '',
    tsvStart: '',
    tsvEnd: '',
    lastModified: Date.now(),
  };
}

export function loadStore(): DraftStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const d = newDraft();
      return { currentId: d.id, drafts: { [d.id]: d } };
    }
    const parsed = JSON.parse(raw) as DraftStore;
    if (!parsed.drafts || Object.keys(parsed.drafts).length === 0) {
      const d = newDraft();
      return { currentId: d.id, drafts: { [d.id]: d } };
    }
    if (!parsed.drafts[parsed.currentId]) {
      parsed.currentId = Object.keys(parsed.drafts)[0];
    }
    // Migrate legacy group names (e.g. "拆除" → "拆除工程")
    for (const id of Object.keys(parsed.drafts)) {
      parsed.drafts[id].gantt = migrateGantt(parsed.drafts[id].gantt);
    }
    return parsed;
  } catch {
    const d = newDraft();
    return { currentId: d.id, drafts: { [d.id]: d } };
  }
}

function migrateGantt(gantt: GanttData): GanttData {
  const out: GanttData = {};
  for (const [oldGroup, row] of Object.entries(gantt)) {
    const newGroup = LEGACY_GROUP_MIGRATION[oldGroup] ?? oldGroup;
    if (!out[newGroup]) out[newGroup] = {};
    for (const [date, items] of Object.entries(row)) {
      const existing = out[newGroup][date] ?? [];
      const merged = [...existing];
      for (const it of items) if (!merged.includes(it)) merged.push(it);
      out[newGroup][date] = merged;
    }
  }
  return out;
}

export function saveStore(store: DraftStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (err) {
    console.warn('[storage] save failed', err);
  }
}
